import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { ProductTier } from '../features/ProductList/components/types';

// Define the shape of our global context state
interface GlobalContextState {
  source: string;
  isMarketplaceUserPaid: boolean;
  isMarketplace: boolean;
  marketplaceUserProjects: string[];
  marketplaceTierType: ProductTier;
  marketplaceUserExpireDate: string;
  tenantId: string;
  tenantData: any | null;
  loading: boolean;
  error: string | null;
  // Marketplace entitlement fields
  accountId?: string;
  entitlementId?: string;
  marketplaceEntitlementId?: string;
  marketplaceEntitlementState?: string;
  marketplaceEntitlementPlan?: string;
  marketplaceEntitlementCreateTime?: string;
  marketplaceEntitlementUpdateTime?: string;
  marketplaceLastUpdated?: string;


}

interface UpdateTenantReturnType {
  source: string;
  isMarketplaceUserPaid: boolean;
  isMarketplace: boolean;
  marketplaceUserProjects: string[];
  marketplaceTierType: ProductTier;
  marketplaceUserExpireDate: string;
  tenantId: string;
  // Marketplace entitlement fields
  accountId?: string;
  entitlementId?: string;
  marketplaceEntitlementId?: string;
  marketplaceEntitlementState?: string;
  marketplaceEntitlementPlan?: string;
  marketplaceEntitlementCreateTime?: string;
  marketplaceEntitlementUpdateTime?: string;
  marketplaceLastUpdated?: string;

  // TODO: Add more data as needed
  marketplaceProduct?: string;
  marketplaceOffer?: string;
  /*
    marketplaceOfferDuration is in standard format ISO 8601 (e.g., "P1Y3M" for 1 year and 3 months).
    Could get the expiration date from the duration like this:
    const duration = dayjs.duration(marketplaceOfferDuration)
    const today = dayjs()
    const expirationDate = today.add(duration) 
  */
  marketplaceOfferDuration?: string;
  marketplaceOrderdId?: string;
  marketplaceEntitlementBenefits?: string[];
}
// Define the shape of our context value
interface GlobalContextValue extends GlobalContextState {
  updateTenantData: (data: any) => UpdateTenantReturnType;
  clearContext: () => void;
}

// Create the context with default values
const GlobalContext = createContext<GlobalContextValue | undefined>(undefined);

// Default state values
const defaultState: GlobalContextState = {
  source: '',
  isMarketplaceUserPaid: false,
  isMarketplace: false,
  marketplaceUserProjects: [],
  marketplaceTierType: 'Basic',
  marketplaceUserExpireDate: new Date(new Date().setMonth(new Date().getMonth() + 1)).toISOString(),
  tenantId: '',
  tenantData: null,
  loading: false,
  error: null,
  // Marketplace entitlement fields initialized as undefined
  accountId: undefined,
  entitlementId: undefined,
  marketplaceEntitlementId: undefined,
  marketplaceEntitlementState: undefined,
  marketplaceEntitlementPlan: undefined,
  marketplaceEntitlementCreateTime: undefined,
  marketplaceEntitlementUpdateTime: undefined,
  marketplaceLastUpdated: undefined,

};

interface GlobalProviderProps {
  children: ReactNode;
}

export const GlobalProvider: React.FC<GlobalProviderProps> = ({ children }) => {
  const [state, setState] = useState<GlobalContextState>(defaultState);

  // Effect to load tenant ID from localStorage on mount
  useEffect(() => {
    const storedTenantId = window.localStorage.getItem('tenantId');
    if (storedTenantId) {
      setState(prev => ({ ...prev, tenantId: storedTenantId }));
    }
  }, []);

  // Helper function to create tenant data object from source data and fallback values
  const createTenantDataObject = (data: any, fallback: GlobalContextState): UpdateTenantReturnType => {
    return {
      source: data?.source || fallback.source,
      isMarketplaceUserPaid: data?.source === 'marketplace' && data?.status === 'Active',
      isMarketplace: data?.source === 'marketplace',
      marketplaceUserProjects: data?.product || fallback.marketplaceUserProjects,
      marketplaceTierType: data?.tier || fallback.marketplaceTierType,
      marketplaceUserExpireDate: data?.subscriptionExpirationDate || fallback.marketplaceUserExpireDate,
      tenantId: data?.tenantId || fallback.tenantId,
      // Marketplace entitlement fields
      accountId: data?.accountId || fallback.accountId,
      entitlementId: data?.entitlementId || fallback.entitlementId,
      marketplaceEntitlementId: data?.marketplaceEntitlementId || fallback.marketplaceEntitlementId,
      marketplaceEntitlementState: data?.marketplaceEntitlementState || fallback.marketplaceEntitlementState,
      marketplaceEntitlementPlan: data?.marketplaceEntitlementPlan || fallback.marketplaceEntitlementPlan,
      marketplaceEntitlementCreateTime: data?.marketplaceEntitlementCreateTime || fallback.marketplaceEntitlementCreateTime,
      marketplaceEntitlementUpdateTime: data?.marketplaceEntitlementUpdateTime || fallback.marketplaceEntitlementUpdateTime,
      marketplaceLastUpdated: data?.marketplaceLastUpdated || fallback.marketplaceLastUpdated,
    };
  };

  // Function to update tenant data manually
  const updateTenantData = (data: any): UpdateTenantReturnType => {
    console.log("updateTenantData ======>", data);

    if (!data) {
      // Return current state values if no data is provided
      return createTenantDataObject(null, state);
    }

    // Update state with new data
    setState(prev => ({
      ...prev,
      ...createTenantDataObject(data, prev),
      tenantData: data, // Keep the original data reference
    }));

    // Return updated values
    return createTenantDataObject(data, state);
  };


  // Function to clear context on logout
  const clearContext = () => {
    setState(defaultState);
  };

  const value = {
    ...state,
    updateTenantData,
    clearContext,
  };

  return (
    <GlobalContext.Provider value={value}>
      {children}
    </GlobalContext.Provider>
  );
};

// Custom hook to use the global context
export const useGlobalContext = (): GlobalContextValue => {
  const context = useContext(GlobalContext);
  if (context === undefined) {
    throw new Error('useGlobalContext must be used within a GlobalProvider');
  }
  return context;
};
